/**
 * @file        src/pages/hotel/SearchLog.jsx
 * @description Hotel Search Log — every customer hotel search (hotel_search_logs),
 *              written by the booking site on each /hotel-searchlist. Legacy
 *              parity: four grouped columns (Client / Segment / Passenger / Other)
 *              with a Details action per row.
 *
 *              Replaces the old "Hotel Search Report", which aggregated BOOKINGS
 *              per hotel and so could never show a search that did not convert —
 *              exactly the thing a search log exists to show.
 * @author      Kinetika Labs
 * @created     2026-07-12
 * @changelog
 *   - 2026-07-12 Claude — Initial (B2C Hotel Management: real search log).
 *   - 2026-08-10 Claude — Added title/subtitle/audience props (defaulting to the
 *                          unchanged B2C behaviour) so CorporateSearchLog.jsx
 *                          can reuse this page fixed to audience=b2b instead of
 *                          forking it — the backend now actually applies
 *                          `audience` (it previously accepted but ignored it).
 */
import { Link } from 'react-router-dom';
import ReadOnlyListPage from '../flight/ReadOnlyListPage';
import Icon from '../../components/Icon';
import { day, dateTime } from './cells';

// One "Label : value" line inside a grouped cell (matches the reference layout).
function L({ k, v }) {
  return (
    <div className="log-line">
      <span className="log-k">{k} :</span> <span className="log-v">{v ?? ''}</span>
    </div>
  );
}

export default function SearchLog({
  title = 'Hotel Search Log',
  subtitle = 'B2C Hotel · customer searches',
  audience = 'b2c',
} = {}) {
  const rowActions = (row) => (
    <div className="row-actions">
      <Link className="btn xs ghost" to={`/hotel/search-log/${row.id}`} title="Details">
        <Icon name="search" /> Details
      </Link>
    </div>
  );

  return (
    <ReadOnlyListPage
      title={title}
      subtitle={subtitle}
      endpoint="/hotel/search-log"
      queryRoot={`hotel-search-log-${audience}`}
      extraParams={{ audience }}
      searchLabel="Location / City Code / Trace ID"
      actionsLabel="Action"
      rowActions={rowActions}
      showIndex={false}
      columns={[
        {
          key: 'id',
          label: 'Client',
          render: (v, r) => (
            <div className="log-cell">
              {/* A guest search has no account, so the legacy console shows 0. */}
              <L k="Customer ID" v={r.user_id || 0} />
              <L k="Customer Name" v={r.customer_name || 'Guest'} />
              <L k="Search ID" v={v} />
              <L k="Entry" v={dateTime(r.created_at)} />
              <L k="Update" v={dateTime(r.updated_at)} />
            </div>
          ),
        },
        {
          key: 'location',
          label: 'Segment',
          render: (v, r) => (
            <div className="log-cell">
              <L k="Location" v={v || '—'} />
              <L k="City Code" v={r.city_code} />
              <L k="Country" v={r.country} />
              <L k="Check In" v={day(r.check_in)} />
              <L k="Check Out" v={day(r.check_out)} />
            </div>
          ),
        },
        {
          key: 'rooms',
          label: 'Passenger',
          render: (v, r) => (
            <div className="log-cell">
              <L k="Room" v={v} />
              <L k="Adult" v={r.num_adults} />
              <L k="Child" v={r.num_children} />
            </div>
          ),
        },
        {
          key: 'trace_id',
          label: 'Other',
          render: (v, r) => (
            <div className="log-cell">
              <L k="Trace ID" v={v} />
              <L k="Status" v={r.status} />
              <L k="Error" v={r.error} />
            </div>
          ),
        },
      ]}
    />
  );
}
